import { z } from 'zod';

import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import {
  departmentFromCommuneCode,
  findCurrentCommuneForHistoricalNir,
} from '@Common/validators/historicalNirCommunes';

/**
 * | NIR Dept |NIR Commune |Commune Name        |Postcode to Allow |
 * | -------- | ---------- | ------------------ | ---------------- |
 * |       03 |        250 |Saint-Pierre-Laval  |            42620 |
 * |       04 |        058 |Claret              |            05110 |
 * |       04 |        066 |Curbans             |            05110 |
 * |       07 |        136 |Laveyrune           |            48250 |
 * |       15 |        105 |Leyvaux             |            43450 |
 * |       24 |        189 |Fougueyrolles       |            33220 |
 * |       26 |        374 |Villebois-les-Pins  |            05700 |
 * |       39 |        274 |Lajoux              |            01410 |
 * |       39 |        102 |Chancia             |            01590 |
 * |       51 |        478 |Saint-Eulien        |            52100 |
 * |       71 |        085 |Change              |            21340 |
 * |       74 |        109 |Éloise              |            01200 |
 * |       83 |        105 |Riboux              |            13780 |
 * |       86 |        042 |Buxeuil             |            37160 |
 * |       91 |        479 |Paray-Vieille-Poste |            94390 |
 */
const departmentExceptionMap: Record<string, string | undefined> = {
  '03250': '42620',
  '04058': '05110',
  '04066': '05110',
  '07136': '48250',
  '15105': '43450',
  '24189': '33220',
  '26374': '05700',
  '39274': '01410',
  '39102': '01590',
  '51478': '52100',
  '71085': '21340',
  '74109': '01200',
  '83105': '13780',
  '86042': '37160',
  '91479': '94390',
};

const ZodNirValidationError = z.enum([
  'format_invalid',
  'gender_invalid',
  'gender_inconsistent',
  'year_invalid',
  'year_inconsistent',
  'month_invalid',
  'month_inconsistent',
  'department_invalid',
  'order_number_invalid',
  'control_key_invalid',
  'invalid',
]);

type NirValidationError = z.infer<typeof ZodNirValidationError>;
export interface NIRValidationContext {
  nir?: string | null;
  gender?: string | null;
  date_of_birth?: Date | null;
  postcode_of_birth?: string | null;
}

export interface NIRValidationResult {
  isValid: boolean;
  errors: NirValidationError[];
}

/**
 * Validates that NIR contains exactly 15 numeric digits
 * but can contain a letter in certain circumstanced: https://en.wikipedia.org/wiki/Departments_of_France#Numbering
 */
function validateNIRFormat(nir: string): boolean {
  return /^\d{5}(?:\d{2}|2[AB])\d{8}$/.test(nir);
}

/**
 * Validates that NIR gender code matches employee gender
 * 1 = male, 2 = female
 */
function validateNIRGender(nir: string, gender: string | null): boolean {
  if (!gender) {
    return false;
  }

  const nirGenderCode = nir.charAt(0);

  if (gender === 'male' && nirGenderCode === '1') {
    return true;
  }
  if (gender === 'female' && nirGenderCode === '2') {
    return true;
  }

  return false;
}

/**
 * Validates that NIR year matches employee birth year
 * Digits 2-3 should match last two digits of birth year
 */
function validateNIRYear(nir: string, dateOfBirth: Date | null): boolean {
  if (!dateOfBirth) {
    return true; // Skip validation if date not provided
  }

  const birthYear = dateOfBirth.getFullYear();
  const nirYear = parseInt(nir.substring(1, 3), 10);
  const expectedYear = birthYear % 100;

  return nirYear === expectedYear;
}

/**
 * Validates NIR month format and consistency
 * Returns: { isValid: boolean, isConsistent: boolean }
 */
function validateNIRMonth(
  nir: string,
  dateOfBirth: Date | null,
): { isValid: boolean; isConsistent: boolean } {
  const nirMonth = parseInt(nir.substring(3, 5), 10);

  // Check if month is in valid range: 01-12 or 20-99
  const isValidMonth = (nirMonth >= 1 && nirMonth <= 12) || (nirMonth >= 20 && nirMonth <= 99);

  if (!isValidMonth) {
    return { isValid: false, isConsistent: false };
  }

  if (!dateOfBirth) {
    return { isValid: true, isConsistent: true }; // Can't check consistency without date
  }

  // Skip consistency check for special month codes (20-99)
  if (nirMonth >= 20) {
    return { isValid: true, isConsistent: true };
  }

  const birthMonth = dateOfBirth.getMonth() + 1; // getMonth() returns 0-11
  const isConsistent = nirMonth === birthMonth;

  return { isValid: true, isConsistent };
}

/**
 * Validates that NIR department code matches postcode of birth
 * Digits 6-7 should match first 2 characters of postcode_of_birth
 * Corsica 2A and 2B are valid codes and should match 20 in the postcode
 * Valid codes: 01-95, 97, 98, 99
 * 97 and 98 are actually prefixes for a 3 digit code, and must match 3 characters of postcode
 * For 97,98 valid ranges are: 971-976, 984-989
 * Some border communes have post codes that differ from department, so we validate those exceptions
 * When departments changed historically (e.g. Seine 75 → 92/93/94 in 1968), accept the NIR
 * commune if it was valid at birth and its current successor department matches the postcode
 */
function validateNIRDepartment(
  nir: string,
  postcodeOfBirth: string | null,
  dateOfBirth: Date | null,
): boolean {
  if (!postcodeOfBirth) {
    return true; // Skip validation if postcode not provided
  }

  let nirDepartment = nir.substring(5, 7);
  let postcodeDepartment = postcodeOfBirth.substring(0, 2);

  // Check if department code is in valid range
  let departmentNum: number;
  if (nirDepartment === '2A' || nirDepartment === '2B') {
    departmentNum = 20;
    nirDepartment = '20';
  } else {
    departmentNum = parseInt(nirDepartment, 10);
  }

  let isValidDepartment = false;
  if (departmentNum === 97 || departmentNum === 98) {
    // These are 3 digit department numbers
    nirDepartment = nir.substring(5, 8);
    postcodeDepartment = postcodeOfBirth.substring(0, 3);

    departmentNum = parseInt(nirDepartment);
    isValidDepartment =
      (departmentNum >= 971 && departmentNum <= 976) ||
      (departmentNum >= 984 && departmentNum <= 989);
  } else if ((departmentNum >= 1 && departmentNum <= 95) || departmentNum === 99) {
    isValidDepartment = true;
  }

  if (!isValidDepartment) {
    return false;
  }

  // Normally first 2 digits of NIR match the postcode, but there are exceptions
  if (nirDepartment === postcodeDepartment) {
    return true;
  }

  const nirCommune = nir.substring(5, 10);
  const mappedPostcode = departmentExceptionMap[nirCommune];
  if (mappedPostcode) {
    return mappedPostcode === postcodeOfBirth;
  }

  // Historical department changes (INSEE COG) — requires birth date (same as before when absent)
  if (!dateOfBirth) {
    return false;
  }

  const currentCommune = findCurrentCommuneForHistoricalNir(nirCommune, dateOfBirth);
  if (!currentCommune) {
    return false;
  }

  return departmentFromCommuneCode(currentCommune) === postcodeDepartment;
}

/**
 * Validates that NIR order number is not 000
 * Digits 11-13 must not equal 000
 */
function validateNIROrderNumber(nir: string): boolean {
  const orderNumber = nir.substring(10, 13);
  return orderNumber !== '000';
}

/**
 * Calculates the NIR control key using modulo 97
 * Control key = 97 - (first 13 digits mod 97)
 */
function calculateNIRControlKey(nir: string): string {
  let first13Digits = nir.substring(0, 13);
  const department = nir.substring(5, 7);
  if (department === '2A' || department === '2B') {
    first13Digits = `${first13Digits.substring(0, 5)}${department === '2A' ? 19 : 18}${first13Digits.substring(7)}`;
  }

  const number = BigInt(first13Digits);
  const remainder = Number(number % 97n);
  const controlKey = 97 - remainder;

  return controlKey.toString().padStart(2, '0');
}

/**
 * Validates that NIR control key is correct
 * Digits 14-15 should match calculated control key
 */
function validateNIRControlKey(nir: string): boolean {
  const providedKey = nir.substring(13, 15);
  const calculatedKey = calculateNIRControlKey(nir);

  return providedKey === calculatedKey;
}

/**
 * Helper function to add validation errors based on context availability
 */
function addValidationError(
  errors: string[],
  isValid: boolean,
  hasContext: boolean,
  basicError: string,
  contextError: string,
): void {
  if (!isValid) {
    errors.push(hasContext ? contextError : basicError);
  }
}

/**
 * Performs all NIR validations except format
 */
function performNIRValidations(
  nir: string,
  gender: string | null,
  date_of_birth: Date | null,
  postcode_of_birth: string | null,
): NirValidationError[] {
  const errors: NirValidationError[] = [];

  // Gender validation - check format validity and consistency if gender is provided
  const nirGenderCode = nir.charAt(0);
  const isValidGenderCode = nirGenderCode === '1' || nirGenderCode === '2';

  if (!isValidGenderCode) {
    errors.push('gender_invalid');
  } else if (gender && !validateNIRGender(nir, gender)) {
    errors.push('gender_inconsistent');
  }

  addValidationError(
    errors,
    validateNIRYear(nir, date_of_birth),
    !!date_of_birth,
    'year_invalid',
    'year_inconsistent',
  );

  // Month validation - check both format and consistency
  const monthValidation = validateNIRMonth(nir, date_of_birth);
  if (!monthValidation.isValid) {
    errors.push('month_invalid');
  } else if (!monthValidation.isConsistent) {
    errors.push('month_inconsistent');
  }

  if (!validateNIRDepartment(nir, postcode_of_birth, date_of_birth)) {
    errors.push('department_invalid');
  }

  if (!validateNIROrderNumber(nir)) {
    errors.push('order_number_invalid');
  }

  if (!validateNIRControlKey(nir)) {
    errors.push('control_key_invalid');
  }

  return errors;
}

/**
 * Comprehensive NIR validation
 * Returns validation result with specific error codes
 */
function validateNIRComplete(context: NIRValidationContext): NIRValidationResult {
  const { nir, gender, date_of_birth, postcode_of_birth } = context;

  if (!nir) {
    return { isValid: true, errors: [] };
  }

  if (!validateNIRFormat(nir)) {
    return { isValid: false, errors: ['format_invalid'] };
  }

  const errors = performNIRValidations(
    nir,
    gender ?? null,
    date_of_birth ?? null,
    postcode_of_birth ?? null,
  );

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Zod schema for NIR validation with context
 */
export function createNIRValidator(context: Omit<NIRValidationContext, 'nir'>) {
  return ZodTrimmedString.nullable().superRefine((nir: string | null, ctx: z.RefinementCtx) => {
    if (!nir) {
      return;
    }

    const result = validateNIRComplete({ ...context, nir });

    if (!result.isValid) {
      result.errors.forEach((error) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: error,
        });
      });
    }
  });
}

export {
  calculateNIRControlKey,
  validateNIRComplete,
  validateNIRControlKey,
  validateNIRDepartment,
  validateNIRFormat,
  validateNIRGender,
  validateNIRMonth,
  validateNIROrderNumber,
  validateNIRYear,
};
